import { FileErrorDescription } from "../interfaces";

const MAX_BITRATE = 25;
const MAX_MB_FILE = 100;

const getBitrate = (videoSize: number, durationInMinutes: number): number => {
  return videoSize / (durationInMinutes * 0.0075);
};

interface DimensionObject {
  width: number;
  height: number;
}

const getVideoDimensions = async (videoUri: string): Promise<DimensionObject | undefined> => {
  const videoElement = document.createElement("video");
  videoElement.classList.add("media-item");

  return new Promise<DimensionObject | undefined>(resolve => {
    try {
      videoElement.addEventListener(
        "loadeddata",
        () => {
          resolve({
            width: videoElement.videoWidth,
            height: videoElement.videoHeight,
          });
        },
        false
      );

      videoElement.preload = "metadata";
      videoElement.muted = true;
      videoElement.setAttribute("crossOrigin", "anonymous");
      videoElement.src = videoUri;
      videoElement.play();
    } catch (error) {
      console.log(error);
      resolve(undefined);
    }
  });
};

const getImageDimensions = async (imageUri: string): Promise<DimensionObject | undefined> => {
  return new Promise<DimensionObject | undefined>(resolve => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = imageUri || "";
    img.onload = () => {
      resolve({
        width: img.width,
        height: img.height,
      });
    };
    img.onerror = () => {
      resolve(undefined);
    };
  });
};

const validateVideo = async (
  file: File,
  maxDuration: number = 300,
  minDuration: number = 3
): Promise<FileErrorDescription> => {
  return new Promise(resolve => {
    const video = document.createElement("video");
    const url = URL.createObjectURL(file);

    video.addEventListener("loadedmetadata", () => {
      URL.revokeObjectURL(url);
      const fileSizeInMG = file.size / 1000000;
      const videoSize = fileSizeInMG / 1024;
      const minutes = video.duration / 60;
      const bitrate = getBitrate(videoSize, minutes); // videoSize / (minutes * 0.0075);
      resolve({
        wrongBitrate: bitrate > MAX_BITRATE,
        wrongLength: video.duration > maxDuration || video.duration < minDuration,
        wrongSize: fileSizeInMG > MAX_MB_FILE,
      });
    });

    video.preload = "metadata";
    video.src = url;
  });
};

function createVideoThumbnailBlob(videoFile: string): Promise<string> {
  const videoElement = document.createElement("video");
  videoElement.classList.add("media-item");
  return new Promise<string>((resolve, reject) => {
    videoElement.addEventListener(
      "loadeddata",
      () => {
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d")!!;
        canvas.width = videoElement.videoWidth;
        canvas.height = videoElement.videoHeight;
        context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
        videoElement.pause();
        const dataURI = canvas.toDataURL("image/jpeg");
        resolve(dataURI);
      },
      false
    );

    videoElement.preload = "metadata";
    videoElement.muted = true;
    videoElement.setAttribute("crossOrigin", "anonymous");
    videoElement.src = videoFile;
    videoElement.play();
  });
}

function dataURLtoBlob(dataurl: string) {
  const arr = dataurl.split(",");
  const mime = arr[0]!!.match(/:(.*?);/)!![1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
}

export { validateVideo, getVideoDimensions, getImageDimensions, createVideoThumbnailBlob, dataURLtoBlob };
